/**
 * Where one tape column came from, in plain words (DOP-627).
 *
 * Replaces the lineage card that printed canonical field names and SQL under
 * the table. The panel leads with what a credit analyst asks — what is this
 * number, and where in the new file is it from — and shows it on the first
 * few loans of the run. The SQL is still there, behind "Show the formula",
 * for the person who wants to audit it.
 */

import { X } from "lucide-react";
import React, { useState } from "react";
import SqlHighlight from "../SqlHighlight";
import type { LoanTapePreviewColumn } from "../Chat/Chat.types";
import {
	ORIGIN_LABEL,
	ORIGIN_PILL,
	columnOrigin,
	sourceLabel,
} from "./loanTapeColumns";

/** How many of the run's rows the panel shows the column on. */
const SAMPLE_ROWS = 5;

export interface ColumnPanelProps {
	column: LoanTapePreviewColumn;
	/** The tape's headers and first rows, to show the column on real loans. */
	columns: readonly LoanTapePreviewColumn[];
	rows: readonly string[][];
	onClose: () => void;
}

const SECTION_HEADING =
	"text-xs font-semibold uppercase tracking-[0.06em] text-[#6B7280]";

const ColumnPanel: React.FC<ColumnPanelProps> = ({
	column,
	columns,
	rows,
	onClose,
}) => {
	const [showFormula, setShowFormula] = useState(false);
	const origin = columnOrigin(column);
	const lineage = column.lineage;
	const index = columns.findIndex((entry) => entry.header === column.header);
	// The first column is the loan's key in every tape the extractor writes
	// (`key_column="Loan ID"`), so it is what each sample row is named by.
	const keyHeader = columns[0]?.header ?? "Loan";
	const sample = index < 0 ? [] : rows.slice(0, SAMPLE_ROWS);

	return (
		<aside
			aria-label="About this column"
			className="flex w-[420px] flex-shrink-0 flex-col border-l border-gray-200 bg-white shadow-[-12px_0_32px_rgba(17,24,39,0.06)]"
		>
			<div className="flex items-start gap-3 border-b border-[#f1f2f4] px-[22px] pb-4 pt-5">
				<div className="min-w-0 flex-1">
					<span
						className={`inline-flex h-[22px] items-center rounded-full px-2 text-[11.5px] font-semibold ${ORIGIN_PILL[origin]}`}
					>
						{ORIGIN_LABEL[origin]}
					</span>
					<h2 className="mt-2 break-words text-xl font-semibold text-[#111111]">
						{column.header}
					</h2>
				</div>
				<button
					type="button"
					onClick={onClose}
					aria-label="Close column"
					className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-[#5B6472] hover:bg-gray-100"
				>
					<X className="h-[18px] w-[18px]" aria-hidden />
				</button>
			</div>

			<div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-[22px] py-[18px]">
				<section className="flex flex-col gap-2">
					<h3 className={SECTION_HEADING}>In plain words</h3>
					<p className="text-[15px] leading-relaxed text-[#1F2937]">
						{origin === "unmapped"
							? "Doppler couldn't find where this column comes from in the new file. Check the example pair has it, then run again."
							: lineage?.transform || "Computed from the new file."}
					</p>
				</section>

				{lineage && lineage.sources.length > 0 && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>Where it's from in the new file</h3>
						<ul className="flex flex-wrap gap-1.5">
							{lineage.sources.map((source) => (
								<li
									key={`${source.tab}:${source.column}:${source.canonical_field}`}
									className="inline-flex h-7 items-center rounded-md border border-[#eceae6] bg-[#f7f7f6] px-2.5 text-[12.5px] text-[#1F2937]"
								>
									{sourceLabel(source)}
								</li>
							))}
						</ul>
					</section>
				)}

				{sample.length > 0 && (
					<section className="flex flex-col gap-2">
						<h3 className={SECTION_HEADING}>See it on real loans</h3>
						<div className="overflow-hidden rounded-[10px] border border-gray-200">
							<table className="w-full border-collapse text-[13px] tabular-nums">
								<thead>
									<tr className="bg-[#fafafa] text-left">
										<th className="px-3 py-2 text-[12.5px] font-semibold text-[#1F2937]">
											{keyHeader}
										</th>
										<th className="px-3 py-2 text-right text-[12.5px] font-semibold text-[#1F2937]">
											In the tape
										</th>
									</tr>
								</thead>
								<tbody>
									{sample.map((row, rowIndex) => (
										// eslint-disable-next-line react/no-array-index-key
										<tr key={rowIndex} className="border-t border-[#f1f2f4]">
											<td className="px-3 py-2 text-[#1F2937]">{row[0]}</td>
											<td className="px-3 py-2 text-right font-semibold text-[#111111]">
												{row[index] === "" || row[index] === undefined ? (
													<span className="font-normal text-[#9a9ea3]">blank</span>
												) : (
													row[index]
												)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</section>
				)}

				{lineage?.sql && (
					<section className="mt-auto flex flex-col gap-2">
						<button
							type="button"
							onClick={() => setShowFormula((open) => !open)}
							aria-expanded={showFormula}
							className="self-start py-1.5 text-[13px] font-medium text-[#004C4B] underline underline-offset-[3px]"
						>
							{showFormula ? "Hide the formula" : "Show the formula"}
						</button>
						{showFormula && <SqlHighlight sql={lineage.sql} />}
					</section>
				)}
			</div>
		</aside>
	);
};

export default ColumnPanel;
